/**
 * Reachability as the PWA sees it (#149, GLOSSARY "Notifying staff"). Pure
 * functions: the store (stores/pushReachability.js) feeds them the server's
 * answer (GET /api/subscriptions/status) and the browser's state.
 *
 * The banner keys on this install: it shows while this install is not a
 * reachable push device, with softer wording when the staff member is
 * reachable on another device. "Require push" (admin-only, per staff member)
 * blocks everything but the push setup screen until this install is one.
 * Both act only under notifications-v2, and neither acts until the server has
 * answered: offline, or with the status request failing, nothing blocks.
 */

/** Routes open while "require push" blocks the PWA. */
export const PUSH_SETUP_ROUTE = 'PushRequired'
const OPEN_WHILE_BLOCKED = new Set(['Login', 'Unlock', PUSH_SETUP_ROUTE])

/**
 * What this browser is, for the guidance: iOS only allows web push from the
 * app installed to the home screen (and only Safari can install it).
 *
 * @param {{userAgent?: string, vendor?: string, platform?: string, maxTouchPoints?: number,
 *          standalone?: boolean, pushSupported?: boolean}} env
 * @returns {{isIos: boolean, isStandalone: boolean, iosNeedsInstall: boolean,
 *          openInSafari: boolean, pushSupported: boolean}}
 */
export function detectInstallContext({ userAgent = '', vendor = '', platform = '', maxTouchPoints = 0, standalone = false, pushSupported = false } = {}) {
    const isIos = /iPad|iPhone|iPod/.test(userAgent) || (platform === 'MacIntel' && maxTouchPoints > 1)
    const isSafari = vendor === 'Apple Computer, Inc.' && !/CriOS\/|FxiOS\/|EdgiOS\//.test(userAgent)
    const iosNeedsInstall = isIos && !standalone
    return {
        isIos,
        isStandalone: !!standalone,
        iosNeedsInstall,
        openInSafari: iosNeedsInstall && !isSafari,
        pushSupported: !!pushSupported
    }
}

/** detectInstallContext for the running browser. */
export function currentInstallContext() {
    if (typeof navigator === 'undefined' || typeof window === 'undefined') return detectInstallContext()
    return detectInstallContext({
        userAgent: navigator.userAgent,
        vendor: navigator.vendor,
        platform: navigator.platform,
        maxTouchPoints: navigator.maxTouchPoints,
        standalone: window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator.standalone === true,
        pushSupported: 'serviceWorker' in navigator && 'PushManager' in window && typeof Notification !== 'undefined'
    })
}

/**
 * The banner to show, or null.
 * kind: 'install' (iOS: add to home screen first, no enable button), 'enable',
 * 'permission-denied' (notifications blocked in the browser's settings) or
 * 'unsupported' (this browser cannot receive pushes).
 *
 * @param {{enabled: boolean, status: object|null, install: object, permission?: string}} state
 * @returns {null|{kind: 'install'|'enable'|'permission-denied'|'unsupported', reachableElsewhere: boolean}}
 */
export function pushBanner({ enabled, status, install, permission = 'default' }) {
    if (!enabled || !status) return null
    if (status.thisDevice === 'reachable') return null
    const reachableElsewhere = !!status.reachable
    let kind = 'enable'
    if (install?.iosNeedsInstall) kind = 'install'
    else if (!install?.pushSupported) kind = 'unsupported'
    else if (permission === 'denied') kind = 'permission-denied'
    return { kind, reachableElsewhere }
}

/**
 * Whether "require push" blocks the PWA: on for this staff member, under
 * notifications-v2, and this install is not a reachable push device.
 * @param {{enabled: boolean, status: object|null}} state
 */
export function shouldBlockForPush({ enabled, status }) {
    return !!(enabled && status?.requirePush && status.thisDevice !== 'reachable')
}

/**
 * Router decision: where to send a navigation, or null to let it through.
 * Blocked: everything but login, PIN unlock and the push setup screen goes to
 * the setup screen. Not blocked: the setup screen has nothing to do, so home.
 * @param {{to: {name?: string}, blocked: boolean}} state
 */
export function pushGateRedirect({ to, blocked }) {
    if (blocked) return OPEN_WHILE_BLOCKED.has(to?.name) ? null : { name: PUSH_SETUP_ROUTE }
    return to?.name === PUSH_SETUP_ROUTE ? { name: 'SelectWeek' } : null
}
